import Cookies from "universal-cookie";
import { AlertModalCheckIcon } from "../../../../assets";
import { AppLayout } from "../../../../containers/layout/app";
import { EditChartOfAccountWrapper } from "./styled";
import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	Box,
	CircularProgress,
	Grid2,
	SelectChangeEvent,
	Typography,
} from "@mui/material";
import { updateChartOfAccountService } from "../../../../utils/api/accounting/chartofaccount/updateChartOfAccount";
import { AppContext } from "../../../../context/appContext";
import { retrieveAllChartOfAccountService } from "../../../../utils/api/accounting/chartofaccount/retrieveAllChartOfAccount";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseOption } from "../../../../components/form/option/styled";
import { BaseAlertModal } from "../../../../components/modal/alert";
import { BaseButton } from "../../../../components/button/styled";
import { retrieveChartOfAccountService } from "../../../../utils/api/accounting/chartofaccount/retrieveChartOfAccount";
import { generateGLCodeService } from "../../../../utils/api/accounting/chartofaccount/generateGLCode";

export const EditChartOfAccount = () => {
	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const glTypes = [
		{
			title: "Asset",
			code: 100,
		},
		{
			title: "Liability",
			code: 200,
		},
		{
			title: "Equity",
			code: 300,
		},
		{
			title: "Income",
			code: 400,
		},
		{
			title: "Expense",
			code: 500,
		},
	];
	const statuses = ["active", "inactive"];
	const allowManualEntriesOptions = ["yes", "no"];
	const glAccountTypes = ["GL Account", "GL Group"];

	const navigate = useNavigate();
	const { id = "" } = useParams();
	const { setActiveNavItem } = useContext(AppContext);

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
	const [loadedGL, setLoadedGL] = useState<Record<string, any> | null>(null);
	const [formInitialized, setFormInitialized] = useState(false);
	const [formDetails, setFormDetails] = useState({
		glName: "",
		glType: " ",
		glAccountType: " ",
		glCode: "",
		glGroup: " ",
		allowManualEntries: " ",
		status: " ",
		description: "",
	});

	useEffect(() => {
		setActiveNavItem("Accounting");
	}, [setActiveNavItem]);

	const retrieveGLCode = async (
		glType: string,
		glAccountType: string,
		glGroup: string,
	) => {
		if (!String(glType).trim() || !glAccountType.trim()) {
			return setFormDetails((prev) => ({
				...prev,
				glCode: "",
			}));
		}

		if (!formInitialized) return;

		const isSameAsLoaded =
			Number(glType) === Number(loadedGL?.glType) &&
			glAccountType === loadedGL?.glAccountType &&
			(glAccountType === "GL Group" || glGroup === loadedGL?.glGroup);

		if (isSameAsLoaded) {
			return setFormDetails((prev) => ({
				...prev,
				glCode: loadedGL?.glCode,
			}));
		}

		if (glAccountType === "GL Account" && glGroup.trim()) {
			try {
				const data = await generateGLCodeService(
					TOKEN,
					glType,
					glAccountType,
					glGroup,
				);
				return setFormDetails((prev) => ({
					...prev,
					glCode: data,
				}));
			} catch (err) {
				console.error("Failed to fetch latest GL:", err);
			}
		}

		if (glAccountType === "GL Group") {
			try {
				const data = await generateGLCodeService(
					TOKEN,
					glType,
					glAccountType,
					" ",
				);
				return setFormDetails((prev) => ({
					...prev,
					glCode: data,
				}));
			} catch (err) {
				console.error("Failed to fetch latest GL:", err);
			}
		}
	};

	useEffect(() => {
		if (!id) return;
		retrieveChartOfAccountService(TOKEN, id)
			.then((data) => {
				setLoadedGL(data);
				setFormDetails(data);
				setFormInitialized(true);
			})
			.catch((err) => {
				console.error("Failed to fetch chart of account:", err);
			});
	}, [TOKEN, id]);

	const { data: glGroups } = useQuery({
		queryKey: ["glGroups", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllChartOfAccountService(TOKEN);
			return response.data;
		},
		enabled: !!TOKEN,
	});

	const handleChange = async (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		const updatedForm = {
			...formDetails,
			[name]: value,
			...(name === "glGroup" && { glCode: "" }),
			...(name === "glType" && { glGroup: " ", glCode: "" }),
			...(name === "glAccountType" && { glGroup: " ", glCode: "" }),
		};
		setFormDetails(updatedForm);

		if (["glType", "glAccountType", "glGroup"].includes(name)) {
			await retrieveGLCode(
				updatedForm.glType,
				updatedForm.glAccountType,
				updatedForm.glGroup,
			);
		}
	};

	const handleAlertModalPersist = () => {
		return setIsAlertModalOpen(true);
	};

	const handleAlertModalCallToActionClick = () => {
		setIsAlertModalOpen(false);
		return navigate(-1);
	};

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		if (!loadedGL?.institution) return;
		try {
			const response = await updateChartOfAccountService(
				TOKEN,
				loadedGL?.institution,
				id,
				formDetails,
			);
			if (response.status === "success") {
				setIsLoading(false);
				setIsAlertModalOpen(true);
			} else {
				setIsLoading(false);
				setError(
					"Failed to add chart of account. Please check credentials and try again.",
				);
			}
		} catch (error: any) {
			setIsLoading(false);
			setError(`Failed to add chart of account. ${error.message}`);
			console.error("Failed to add chart of account:", error);
		}
	};
	return (
		<AppLayout pageId={"Chart-of-Account"} pageTitle={"Edit Chart of Account"}>
			<EditChartOfAccountWrapper>
				{/* alert modal below */}
				<BaseAlertModal
					open={isAlertModalOpen}
					icon={<AlertModalCheckIcon />}
					title="Chart of Account Successfully Updated"
					message="Your information has been successfully added to the system."
					callToAction="Back to Charts of Account"
					handleClose={handleAlertModalPersist}
					handleCallToAction={handleAlertModalCallToActionClick}
				/>
				{/* alert modal above */}
				<Box>
					<Typography
						variant="h3"
						fontFamily={"Poppins"}
						fontWeight={500}
						fontSize={18}
						lineHeight={"normal"}
						color="var(--text-salmon-pink-color)"
					>
						Enter Required Information to update chart of account.
					</Typography>
				</Box>
				<form onSubmit={handleSubmit}>
					<Grid2
						container
						component={"div"}
						spacing={"var(--flex-gap)"}
						className="fieldset-grid-wrapper"
					>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">GL Name</BaseLabel>
								<BaseInput
									required
									name="glName"
									value={formDetails.glName
										?.split(" ")
										.map(
											(word: string) =>
												word.charAt(0).toUpperCase() + word.slice(1),
										)
										.join(" ")}
									placeholder="Enter GL Name"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">GL Type</BaseLabel>
								<BaseSelect
									name="glType"
									fontweight={400}
									value={formDetails.glType}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select GL Type</BaseOption>
									{glTypes?.map((type: Record<string, any>, index: number) => {
										return (
											<BaseOption key={index} value={type.code}>
												{type.title}
											</BaseOption>
										);
									})}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">GL Account</BaseLabel>
								<BaseSelect
									name="glAccountType"
									fontweight={400}
									required
									value={formDetails.glAccountType}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select GL Account</BaseOption>
									{glAccountTypes?.map((accountType: string, index: number) => {
										return (
											<BaseOption key={index} value={accountType}>
												{accountType}
											</BaseOption>
										);
									})}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12, tablet: 6 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">GL Code</BaseLabel>
								<BaseInput
									readOnly
									required
									name="glCode"
									value={formDetails.glCode}
									placeholder="Code"
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
						{formDetails.glAccountType === "GL Account" && (
							<Grid2
								size={{
									mobile: 12,
									tablet: formDetails.glAccountType === "GL Account" ? 4 : 6,
								}}
							>
								<BaseFieldSet>
									<BaseLabel colour="var(--dark-color)">GL Group</BaseLabel>
									<BaseSelect
										name="glGroup"
										fontweight={400}
										value={
											formDetails.glAccountType === "GL Account" &&
											formDetails.glGroup
										}
										colour="var(--semi-gray-color)"
										onChange={(e) => handleChange(e)}
									>
										<BaseOption value={" "}>Select GL Group</BaseOption>
										{glGroups
											?.filter(
												(group: Record<string, any>) =>
													group.glAccountType === "GL Group" &&
													group.glType === String(formDetails.glType),
											)
											?.map((group: Record<string, any>, index: number) => {
												return (
													<BaseOption key={index} value={group.id}>
														{group.glName
															?.split(" ")
															.map(
																(word: string) =>
																	word.charAt(0).toUpperCase() + word.slice(1),
															)
															.join(" ")}
													</BaseOption>
												);
											})}
									</BaseSelect>
								</BaseFieldSet>
							</Grid2>
						)}
						<Grid2
							size={{
								mobile: 12,
								tablet: formDetails.glAccountType === "GL Account" ? 4 : 6,
							}}
						>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">
									Allow Manual Entries
								</BaseLabel>
								<BaseSelect
									name="allowManualEntries"
									fontweight={400}
									value={formDetails.allowManualEntries}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select Option</BaseOption>
									{allowManualEntriesOptions?.map(
										(option: string, index: number) => {
											return (
												<BaseOption key={index} value={option}>
													{option.charAt(0).toUpperCase() +
														option.slice(1).toLowerCase()}
												</BaseOption>
											);
										},
									)}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2
							size={{
								mobile: 12,
								tablet: formDetails.glAccountType === "GL Account" ? 4 : 6,
							}}
						>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Status</BaseLabel>
								<BaseSelect
									name="status"
									fontweight={400}
									value={formDetails.status}
									colour="var(--semi-gray-color)"
									onChange={(e) => handleChange(e)}
								>
									<BaseOption value={" "}>Select Option</BaseOption>
									{statuses?.map((status: string, index: number) => {
										return (
											<BaseOption key={index} value={status}>
												{status.charAt(0).toUpperCase() +
													status.slice(1).toLowerCase()}
											</BaseOption>
										);
									})}
								</BaseSelect>
							</BaseFieldSet>
						</Grid2>
						<Grid2 size={{ mobile: 12 }}>
							<BaseFieldSet>
								<BaseLabel colour="var(--dark-color)">Description</BaseLabel>
								<BaseInput
									required
									multiline
									minRows={4}
									name="description"
									value={formDetails.description}
									placeholder="Add any additional details or notes about the account."
									onChange={(e) => handleChange(e)}
								/>
							</BaseFieldSet>
						</Grid2>
					</Grid2>
					{error && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"var(--text-salmon-pink-color)"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
					<Box overflow={"hidden"}>
						<BaseButton
							type="submit"
							variant="contained"
							bgcolor="var(--primary-color)"
							disableElevation
							sx={{
								width: { mobile: "100%", miniTablet: "auto" },
							}}
						>
							{isLoading ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Submit
								</Typography>
							)}
						</BaseButton>
					</Box>
				</form>
			</EditChartOfAccountWrapper>
		</AppLayout>
	);
};
